import { useState, useRef } from 'react';
import { Plus, Trash, PencilSimple, FolderPlus, CaretRight, CaretDown, Folder, FolderOpen } from '@phosphor-icons/react';
import type { Folder as FolderType } from '@spine-workspace/shared';
import { useAssetStore } from '../../stores/assetStore';

interface FolderNodeProps {
  folder: FolderType;
  depth: number;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onDropAssets: (assetIds: string[], folderId: string) => void;
  onDropFolder: (draggedFolderId: string, targetFolderId: string) => void;
}

function parseAssetDrop(e: React.DragEvent): string[] {
  const raw = e.dataTransfer.getData('application/spine-asset');
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    const arr = Array.isArray(parsed) ? parsed : [parsed];
    return arr.map((a: { id?: string }) => a?.id).filter(Boolean) as string[];
  } catch { return []; }
}

function FolderNode({ folder, depth, selectedId, onSelect, onDropAssets, onDropFolder }: FolderNodeProps) {
  const { renameFolder, deleteFolder, createFolder } = useAssetStore();
  const isSelected = selectedId === folder.id;
  const children = folder.children ?? [];

  const [collapsed, setCollapsed] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState('');
  const [creatingChild, setCreatingChild] = useState(false);
  const [childDraft, setChildDraft] = useState('');
  const [dropOver, setDropOver] = useState(false);
  const [hovered, setHovered] = useState(false);
  const dragCounter = useRef(0);
  const renameCancelledRef = useRef(false);

  function startRename(e: React.MouseEvent) {
    e.stopPropagation();
    setDraft(folder.name);
    setRenaming(true);
  }

  async function commitRename() {
    if (renameCancelledRef.current) { renameCancelledRef.current = false; return; }
    const trimmed = draft.trim();
    if (trimmed && trimmed !== folder.name) await renameFolder(folder.id, trimmed);
    setRenaming(false);
  }

  async function handleDelete(e: React.MouseEvent) {
    e.stopPropagation();
    if (confirm(`Delete folder "${folder.name}"? Assets inside will move to root.`)) {
      await deleteFolder(folder.id);
      if (selectedId === folder.id) onSelect(null);
    }
  }

  async function commitChildCreate() {
    const trimmed = childDraft.trim();
    if (trimmed) await createFolder(trimmed, folder.id);
    setChildDraft('');
    setCreatingChild(false);
    setCollapsed(false);
  }

  function isValidDrop(e: React.DragEvent) {
    return (
      e.dataTransfer.types.includes('application/spine-asset') ||
      e.dataTransfer.types.includes('application/spine-folder')
    );
  }

  function handleDragEnter(e: React.DragEvent) {
    if (isValidDrop(e)) {
      dragCounter.current++;
      setDropOver(true);
    }
  }

  function handleDragOver(e: React.DragEvent) {
    if (isValidDrop(e)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    }
  }

  function handleDragLeave() {
    dragCounter.current--;
    if (dragCounter.current === 0) setDropOver(false);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    dragCounter.current = 0;
    setDropOver(false);

    // Folder-to-folder drop
    const folderRaw = e.dataTransfer.getData('application/spine-folder');
    if (folderRaw) {
      try {
        const dragged = JSON.parse(folderRaw) as { id: string };
        if (dragged?.id && dragged.id !== folder.id) {
          onDropFolder(dragged.id, folder.id);
        }
      } catch { /* ignore */ }
      return;
    }

    // Asset(s) drop
    const assetIds = parseAssetDrop(e);
    if (assetIds.length > 0) onDropAssets(assetIds, folder.id);
  }

  const hasChildren = children.length > 0;

  return (
    <div>
      <div
        data-testid={`folder-row-${folder.id}`}
        draggable
        className={`w-full flex items-center gap-1 px-1 py-0.5 rounded text-sm transition-colors cursor-pointer group ${
          isSelected
            ? 'bg-accent/10 text-accent'
            : dropOver
              ? 'bg-accent/20 text-accent'
              : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
        }`}
        style={{ paddingLeft: `${4 + depth * 12}px` }}
        onClick={() => !renaming && onSelect(isSelected ? null : folder.id)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onDragStart={(e) => {
          e.stopPropagation();
          e.dataTransfer.setData('application/spine-folder', JSON.stringify({ id: folder.id, name: folder.name }));
          e.dataTransfer.effectAllowed = 'move';
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Collapse chevron */}
        <button
          onClick={(e) => { e.stopPropagation(); setCollapsed((c) => !c); }}
          className="shrink-0 w-4 h-4 flex items-center justify-center text-text-tertiary hover:text-text-primary"
        >
          {hasChildren
            ? collapsed ? <CaretRight size={10} /> : <CaretDown size={10} />
            : <span className="w-2" />
          }
        </button>

        {/* Folder icon */}
        <span className="shrink-0">
          {isSelected || !collapsed
            ? <FolderOpen size={13} className={isSelected ? 'text-accent' : 'text-text-tertiary'} />
            : <Folder size={13} className={isSelected ? 'text-accent' : 'text-text-tertiary'} />
          }
        </span>

        {/* Name or rename input */}
        {renaming ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
              if (e.key === 'Escape') { renameCancelledRef.current = true; setRenaming(false); e.currentTarget.blur(); }
              e.stopPropagation();
            }}
            onBlur={commitRename}
            onClick={(e) => e.stopPropagation()}
            data-testid={`folder-name-input-${folder.id}`}
            className="flex-1 min-w-0 bg-bg-input border border-accent rounded px-1 py-0 text-xs font-sans text-text-primary outline-none"
          />
        ) : (
          <span
            data-testid={`folder-name-${folder.id}`}
            className="flex-1 min-w-0 truncate text-xs font-sans"
            onDoubleClick={startRename}
          >
            {folder.name}
          </span>
        )}

        {/* Actions (on hover) */}
        {hovered && !renaming && (
          <div className="flex items-center gap-0.5 shrink-0">
            <button
              title="New subfolder"
              data-testid={`folder-add-child-${folder.id}`}
              onClick={(e) => { e.stopPropagation(); setCreatingChild(true); setCollapsed(false); }}
              className="w-[18px] h-[18px] flex items-center justify-center rounded text-text-tertiary hover:text-accent hover:bg-accent-muted transition-all"
            >
              <FolderPlus size={11} />
            </button>
            <button
              title="Rename folder"
              data-testid={`folder-rename-${folder.id}`}
              onClick={startRename}
              className="w-[18px] h-[18px] flex items-center justify-center rounded text-text-tertiary hover:text-accent hover:bg-accent-muted transition-all"
            >
              <PencilSimple size={11} />
            </button>
            <button
              title="Delete folder"
              data-testid={`folder-delete-${folder.id}`}
              onClick={handleDelete}
              className="w-[18px] h-[18px] flex items-center justify-center rounded text-text-tertiary hover:text-error hover:bg-error-muted transition-all"
            >
              <Trash size={11} />
            </button>
          </div>
        )}
      </div>

      {/* Create child input */}
      {creatingChild && (
        <div style={{ paddingLeft: `${4 + (depth + 1) * 12 + 4}px` }} className="pr-1 py-0.5">
          <input
            autoFocus
            value={childDraft}
            onChange={(e) => setChildDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
              if (e.key === 'Escape') { setCreatingChild(false); setChildDraft(''); e.currentTarget.blur(); }
              e.stopPropagation();
            }}
            onBlur={commitChildCreate}
            placeholder="Folder name…"
            data-testid={`folder-create-child-input-${folder.id}`}
            className="w-full bg-bg-input border border-accent rounded px-1.5 py-0.5 text-xs font-sans text-text-primary outline-none"
          />
        </div>
      )}

      {/* Children */}
      {!collapsed && children.map((child) => (
        <FolderNode
          key={child.id}
          folder={child}
          depth={depth + 1}
          selectedId={selectedId}
          onSelect={onSelect}
          onDropAssets={onDropAssets}
          onDropFolder={onDropFolder}
        />
      ))}
    </div>
  );
}

function buildTree(folders: FolderType[]): FolderType[] {
  const map = new Map<string, FolderType>(folders.map((f) => [f.id, { ...f, children: [] }]));
  const roots: FolderType[] = [];
  for (const f of map.values()) {
    if (f.parentId) {
      map.get(f.parentId)?.children?.push(f);
    } else {
      roots.push(f);
    }
  }
  return roots;
}

export default function FolderTree() {
  const folders = useAssetStore((s) => s.folders);
  const selectedFolderId = useAssetStore((s) => s.selectedFolderId);
  const setSelectedFolder = useAssetStore((s) => s.setSelectedFolder);
  const { createFolder, moveAssetsToFolder, moveFolder } = useAssetStore();
  const [creatingRoot, setCreatingRoot] = useState(false);
  const [rootDraft, setRootDraft] = useState('');
  const [dropOverRoot, setDropOverRoot] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function commitRootCreate() {
    const trimmed = rootDraft.trim();
    if (trimmed) await createFolder(trimmed);
    setRootDraft('');
    setCreatingRoot(false);
  }

  const tree = buildTree(folders);

  function isRootDrop(e: React.DragEvent) {
    return (
      e.dataTransfer.types.includes('application/spine-asset') ||
      e.dataTransfer.types.includes('application/spine-folder')
    );
  }

  function handleDropOnRoot(e: React.DragEvent) {
    e.preventDefault();
    setDropOverRoot(false);

    // Folder drop → move to root
    const folderRaw = e.dataTransfer.getData('application/spine-folder');
    if (folderRaw) {
      try {
        const dragged = JSON.parse(folderRaw) as { id: string };
        if (dragged?.id) moveFolder(dragged.id, null);
      } catch { /* ignore */ }
      return;
    }

    // Asset(s) drop → move to root (no folder)
    const assetRaw = e.dataTransfer.getData('application/spine-asset');
    if (!assetRaw) return;
    try {
      const parsed = JSON.parse(assetRaw);
      const arr = Array.isArray(parsed) ? parsed : [parsed];
      const ids = arr.map((a: { id?: string }) => a?.id).filter(Boolean) as string[];
      if (ids.length > 0) moveAssetsToFolder(ids, null);
    } catch { /* ignore */ }
  }

  return (
    <div className="mb-1">
      {/* Header row with "All Assets" + new folder button */}
      <div className="flex items-center gap-1 mb-0.5">
        <button
          className={`flex-1 text-left flex items-center gap-1.5 px-2 py-1 rounded text-sm transition-colors ${
            selectedFolderId === null
              ? 'text-accent'
              : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
          } ${dropOverRoot ? 'bg-accent/20' : ''}`}
          onClick={() => setSelectedFolder(null)}
          onDragOver={(e) => { if (isRootDrop(e)) { e.preventDefault(); setDropOverRoot(true); } }}
          onDragLeave={() => setDropOverRoot(false)}
          onDrop={handleDropOnRoot}
          data-testid="folder-all-assets"
        >
          <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          All Assets
        </button>
        <button
          title="New folder"
          data-testid="folder-create-btn"
          onClick={() => { setCreatingRoot(true); setTimeout(() => inputRef.current?.focus(), 0); }}
          className="w-[20px] h-[20px] flex items-center justify-center rounded text-text-tertiary hover:text-accent hover:bg-accent-muted transition-all duration-fast shrink-0"
        >
          <Plus size={11} />
        </button>
      </div>

      {/* Root folder creation input */}
      {creatingRoot && (
        <div className="px-1 py-0.5 mb-0.5">
          <input
            ref={inputRef}
            autoFocus
            value={rootDraft}
            onChange={(e) => setRootDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
              if (e.key === 'Escape') { setCreatingRoot(false); setRootDraft(''); e.currentTarget.blur(); }
              e.stopPropagation();
            }}
            onBlur={commitRootCreate}
            placeholder="Folder name…"
            data-testid="folder-create-root-input"
            className="w-full bg-bg-input border border-accent rounded px-1.5 py-0.5 text-xs font-sans text-text-primary outline-none"
          />
        </div>
      )}

      {tree.map((folder) => (
        <FolderNode
          key={folder.id}
          folder={folder}
          depth={0}
          selectedId={selectedFolderId}
          onSelect={setSelectedFolder}
          onDropAssets={(assetIds, folderId) => moveAssetsToFolder(assetIds, folderId)}
          onDropFolder={(draggedId, targetId) => moveFolder(draggedId, targetId)}
        />
      ))}
    </div>
  );
}
